import React, { useMemo, useRef, useState, useEffect } from "react";
import api from "@/lib/api";
import { PageHeader, Pill } from "@/components/kit";
import { usd } from "@/lib/format";
import { Sparkles, Upload, Loader2, Wand2, ImageIcon, Layers, Building2, Palette, Trash2, MapPin } from "lucide-react";
import { toast } from "sonner";
import { CATALOG, CATEGORIES } from "@/lib/roofCatalog";
import { tLabel } from "@/lib/roofTypes";
import AddressAutocomplete from "@/components/AddressAutocomplete";

const brands0 = (cat) => Object.keys(CATALOG[cat] || {})[0];
const product0 = (cat, b) => Object.keys(CATALOG[cat]?.[b] || {})[0];

const PRICING = [
  { tier: "SHINGLE", name: "Architectural Shingle", price: 1890000 },
  { tier: "METAL", name: "Standing Seam Metal", price: 3150000 },
  { tier: "TILE", name: "Clay / Concrete Tile", price: 4480000 },
  { tier: "FLAT", name: "TPO Membrane (Flat)", price: 2980000 },
];
const TIER_TONE = { SHINGLE: "navy", METAL: "slate", TILE: "red", FLAT: "slate", GOOD: "slate", BETTER: "navy", BEST: "red" };
const readFile = (file) => new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(file); });
const selCls = "w-full border-2 border-slate-200 rounded-xl px-3 py-2.5 text-sm font-semibold text-navy outline-none focus:border-navy bg-white";

export default function DesignTool() {
  const [src, setSrc] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [contactId, setContactId] = useState("");
  const [saving, setSaving] = useState(false);
  const [addr, setAddr] = useState("");
  const [coords, setCoords] = useState(null);
  const fileRef = useRef(null);

  const [category, setCategory] = useState(CATEGORIES[0]);
  const [brand, setBrand] = useState(brands0(CATEGORIES[0]));
  const [product, setProduct] = useState(product0(CATEGORIES[0], brands0(CATEGORIES[0])));
  const [color, setColor] = useState(null);

  const brands = useMemo(() => Object.keys(CATALOG[category] || {}), [category]);
  const brandSafe = CATALOG[category]?.[brand] ? brand : brands[0];
  const products = useMemo(() => Object.keys(CATALOG[category]?.[brandSafe] || {}), [category, brandSafe]);
  const productSafe = CATALOG[category]?.[brandSafe]?.[product] ? product : products[0];
  const colors = useMemo(() => CATALOG[category]?.[brandSafe]?.[productSafe] || [], [category, brandSafe, productSafe]);
  useEffect(() => { if (!colors.find((x) => x.name === color?.name)) setColor(colors[0]); }, [colors]); // eslint-disable-line

  const pickCategory = (cat) => { setCategory(cat); setBrand(brands0(cat)); setProduct(product0(cat, brands0(cat))); setResult(null); };
  const pickBrand = (b) => { setBrand(b); setProduct(product0(category, b)); setResult(null); };
  const pickProduct = (p) => { setProduct(p); setResult(null); };

  const matLabel = `${brandSafe} ${productSafe}`;
  const pick = async (e) => { const f = e.target.files?.[0]; if (!f) return; setSrc(await readFile(f)); setResult(null); };

  const render = async () => {
    if (!src) return toast.error("Upload a property photo first");
    if (!color) return toast.error("Pick a color");
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/design/visualize", { image_base64: src, material_name: matLabel, color: color.name, color_hex: color.hex });
      setResult(data.image); toast.success("Visualization ready");
    } catch (e) { toast.error("Visualization failed — try another photo"); } finally { setBusy(false); }
  };

  useEffect(() => { api.get("/contacts").then((r) => setContacts(r.data)).catch(() => {}); }, []);

  const saveToCustomer = async () => {
    if (!contactId) return toast.error("Pick a customer to attach this to");
    if (!result) return toast.error("Render a visualization first");
    setSaving(true);
    try {
      await api.post("/design/save", { contact_id: contactId, before: src, after: result, material: matLabel, color: color?.name });
      toast.success("Saved to customer's proposal — it will show on their proposal page & PDF");
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  return (
    <div>
      <PageHeader title="AI Design & Price" subtitle="Upload a home photo → pick a brand, product & color → AI renders the new roof" />

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <div className="mb-card p-5">
            <input ref={fileRef} type="file" accept="image/*" onChange={pick} className="hidden" data-testid="design-file-input" />
            {!src ? (
              <button data-testid="upload-home-btn" onClick={() => fileRef.current?.click()}
                className="w-full h-56 border-2 border-dashed border-slate-300 rounded-xl flex flex-col items-center justify-center gap-2 text-slate-400 hover:border-navy hover:text-navy transition-colors">
                <Upload className="w-8 h-8" /> <span className="font-semibold">Upload a property photo</span>
                <span className="text-xs">or search an address below to grab a Street View photo</span>
              </button>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <div><div className="text-xs font-semibold uppercase text-slate-400 mb-1">Before</div><img src={src} alt="before" className="w-full h-56 object-cover rounded-xl" /></div>
                <div><div className="text-xs font-semibold uppercase text-slate-400 mb-1 truncate">After · {color?.name}</div>
                  <div className="w-full h-56 rounded-xl bg-slate-100 flex items-center justify-center overflow-hidden">
                    {busy ? <Loader2 className="w-8 h-8 animate-spin text-navy" /> : result ? <img src={result} alt="after" className="w-full h-full object-cover" data-testid="after-image" /> : <ImageIcon className="w-8 h-8 text-slate-300" />}
                  </div></div>
              </div>
            )}
            {src && (
              <div className="flex items-center gap-4 mt-2">
                <button data-testid="change-photo-btn" onClick={() => fileRef.current?.click()} className="text-xs text-navy font-semibold">Change photo</button>
                <button data-testid="remove-photo-btn" onClick={() => { setSrc(null); setResult(null); if (fileRef.current) fileRef.current.value = ""; }} className="text-xs text-brandred font-semibold inline-flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> Remove photo</button>
              </div>
            )}

            <div className="mt-4 pt-4 border-t border-slate-100" data-testid="streetview-search">
              <label className="text-xs font-semibold uppercase text-slate-500 flex items-center gap-1 mb-2"><MapPin className="w-3.5 h-3.5" /> Find a house by address</label>
              <div className="flex flex-col sm:flex-row gap-2">
                <AddressAutocomplete value={addr} onChange={setAddr} onSelect={(p) => setCoords({ lat: p.lat, lon: p.lon })}
                  placeholder="Type a property address…" testid="design-address-input"
                  className="flex-1 w-full border-2 border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-navy" />
                <button data-testid="open-streetview-btn" disabled={!coords}
                  onClick={() => coords && window.open(`https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${coords.lat},${coords.lon}`, "_blank")}
                  className="bg-navy text-white font-bold px-4 py-2.5 rounded-xl border-2 border-navy inline-flex items-center justify-center gap-2 active:translate-y-px disabled:opacity-50 whitespace-nowrap">
                  <MapPin className="w-4 h-4" /> Open Street View
                </button>
              </div>
              <p className="text-[11px] text-slate-400 mt-2">Opens Google Street View at that address in a new tab — position the view on the house, take a screenshot, then upload it above.</p>
            </div>
          </div>

          {/* Cascading selection: Category -> Brand -> Product -> Color */}
          <div className="mb-card p-5 space-y-4">
            <div>
              <label className="text-xs font-semibold uppercase text-slate-500 flex items-center gap-1 mb-2"><Layers className="w-3.5 h-3.5" /> Material</label>
              <div className="flex flex-wrap gap-2">
                {CATEGORIES.map((cat) => (
                  <button key={cat} data-testid={`category-${cat}`} onClick={() => pickCategory(cat)}
                    className={`px-4 py-2 rounded-xl border-2 text-sm font-semibold transition-all ${category === cat ? "border-navy bg-navy text-white" : "border-slate-200 bg-white text-navy hover:border-navy"}`}>
                    {cat}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold uppercase text-slate-500 flex items-center gap-1 mb-1.5"><Building2 className="w-3.5 h-3.5" /> Brand</label>
                <select data-testid="brand-select" value={brandSafe} onChange={(e) => pickBrand(e.target.value)} className={selCls}>
                  {brands.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase text-slate-500 flex items-center gap-1 mb-1.5"><Sparkles className="w-3.5 h-3.5" /> Product</label>
                <select data-testid="product-select" value={productSafe} onChange={(e) => pickProduct(e.target.value)} className={selCls}>
                  {products.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold uppercase text-slate-500 flex items-center gap-1 mb-2"><Palette className="w-3.5 h-3.5" /> Color · <span className="text-navy">{color?.name}</span></label>
              <div className="flex flex-wrap gap-2">
                {colors.map((col) => (
                  <button key={col.name} data-testid={`color-${col.name.replace(/\s+/g, "-").toLowerCase()}`} onClick={() => setColor(col)} title={col.name}
                    className={`flex items-center gap-2 pl-1.5 pr-3 py-1.5 rounded-full border-2 text-xs font-semibold transition-all ${color?.name === col.name ? "border-navy bg-white shadow" : "border-slate-200 bg-white hover:border-slate-300"}`}>
                    <span className="w-6 h-6 rounded-full border border-slate-300 shrink-0" style={{ background: col.hex }} />
                    <span className="text-navy">{col.name}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <button data-testid="render-btn" onClick={render} disabled={busy || !src}
            className="w-full bg-brandred text-white font-bold py-3.5 rounded-xl border-2 border-brandred flex items-center justify-center gap-2 active:translate-y-px disabled:opacity-60">
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Wand2 className="w-5 h-5" />} Render Before / After with AI
          </button>

          {result && (
            <div className="mb-card p-4 flex flex-col sm:flex-row gap-3 sm:items-center" data-testid="save-design-block">
              <select data-testid="design-customer-select" value={contactId} onChange={(e) => setContactId(e.target.value)} className={selCls + " flex-1"}>
                <option value="">Attach to customer…</option>
                {contacts.map((cc) => <option key={cc.id} value={cc.id}>{cc.name}</option>)}
              </select>
              <button data-testid="save-design-btn" onClick={saveToCustomer} disabled={saving || !contactId}
                className="bg-navy text-white font-bold px-5 py-3 rounded-xl border-2 border-navy flex items-center justify-center gap-2 active:translate-y-px disabled:opacity-60 whitespace-nowrap">
                {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Building2 className="w-5 h-5" />} Save to Proposal
              </button>
            </div>
          )}
        </div>

        {/* Good/Better/Best price cards */}
        <div className="space-y-3">
          <div className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-navy" /><h3 className="font-display font-bold uppercase text-lg text-navy">Your Options</h3></div>
          <div className="mb-card p-3 border-2 border-navy/20 bg-navy/5">
            <div className="text-[11px] uppercase font-semibold text-slate-500">Selected</div>
            <div className="flex items-center gap-2 mt-1">
              <span className="w-6 h-6 rounded-full border border-slate-300 shrink-0" style={{ background: color?.hex }} />
              <div className="min-w-0"><div className="text-sm font-bold text-navy truncate">{matLabel}</div><div className="text-xs text-slate-500">{color?.name}</div></div>
            </div>
          </div>
          {PRICING.map((m) => (
            <div key={m.tier} data-testid={`price-card-${m.tier}`} className="mb-card p-4 border-2 border-transparent">
              <div className="flex justify-between items-center"><Pill tone={TIER_TONE[m.tier]}>{tLabel(m.tier)}</Pill></div>
              <div className="text-sm text-slate-500 mt-2">{m.name}</div>
              <div className="font-num text-3xl font-extrabold text-navy mt-1">{usd(m.price)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
